import React, { useState, useRef, useEffect } from 'react';
import { MapContainer, TileLayer, Polyline, Marker, Popup, useMap } from 'react-leaflet';
import { X, Check, Plus, Trash2, Navigation, Save, Ruler } from 'lucide-react';
import L from 'leaflet';

// Configurar ícones do Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.8.0/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.8.0/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.8.0/images/marker-shadow.png',
});

// Componente para capturar cliques no mapa
function MapClickHandler({ onMapClick, ativo }) {
  const map = useMap();

  useEffect(() => {
    if (!ativo) return;

    const handleClick = (e) => {
      onMapClick([e.latlng.lat, e.latlng.lng]);
    };

    map.on('click', handleClick);
    return () => map.off('click', handleClick);
  }, [map, onMapClick, ativo]);

  return null;
}

const DesenharTrajeto = ({ onTrechosAtualizados, onFechar, titulo, trechosIniciais = [] }) => {
  const [trechos, setTrechos] = useState(trechosIniciais);
  const [trechoAtivo, setTrechoAtivo] = useState(null);
  const [modoEdicao, setModoEdicao] = useState('visualizar');
  const [pontosMarcados, setPontosMarcados] = useState([]);
  const [segmentos, setSegmentos] = useState([]);
  const [nomeNovoTrecho, setNomeNovoTrecho] = useState('');
  const [nomeNovaDerivacao, setNomeNovaDerivacao] = useState('');
  const [carregandoRota, setCarregandoRota] = useState(false);
  
  // Coordenadas de Conceição do Castelo - ES
  const centroConceicaoCastelo = [-20.359292, -41.247282];

  // Função para calcular rota entre dois pontos usando OSRM
  const calcularRotaEntrePontos = async (pontoInicial, pontoFinal) => {
    if (!pontoInicial || !pontoFinal) return [pontoInicial, pontoFinal];

    setCarregandoRota(true);
    try {
      // Usando OSRM (Open Source Routing Machine) - gratuito
      // Usando o perfil 'walking' para ignorar restrições de direção de trânsito
      const coordenadas = `${pontoInicial[1]},${pontoInicial[0]};${pontoFinal[1]},${pontoFinal[0]}`;
      const url = `https://router.project-osrm.org/route/v1/walking/${coordenadas}?overview=full&geometries=geojson`;
      
      const response = await fetch(url);
      const data = await response.json();
      
      if (data.routes && data.routes[0]) {
        const geometry = data.routes[0].geometry;
        // Converter coordenadas GeoJSON para formato Leaflet [lat, lng]
        const rotaPontos = geometry.coordinates.map(coord => [coord[1], coord[0]]);
        return rotaPontos;
      }
    } catch (error) {
      console.warn('Erro ao calcular rota automática, usando pontos diretos:', error);
    } finally {
      setCarregandoRota(false);
    }
    
    // Se falhar, retorna os pontos originais
    return [pontoInicial, pontoFinal];
  };

  // Função para adicionar um novo ponto e calcular o segmento até ele
  const adicionarPonto = async (novoPonto) => {
    // Se é o primeiro ponto, apenas adicionar
    if (pontosMarcados.length === 0) {
      setPontosMarcados([novoPonto]);
      return;
    }

    // Pegar o último ponto marcado
    const ultimoPonto = pontosMarcados[pontosMarcados.length - 1];
    
    // Calcular rota entre o último ponto e o novo ponto
    const novoSegmento = await calcularRotaEntrePontos(ultimoPonto, novoPonto);
    
    // Adicionar o novo segmento
    setSegmentos([...segmentos, novoSegmento]);
    
    // Adicionar o novo ponto à lista de pontos marcados
    setPontosMarcados([...pontosMarcados, novoPonto]);
  };

  const handleMapClick = async (coordenadas) => {
    if (modoEdicao === 'novo-trecho') {
      await adicionarPonto(coordenadas);
    } else if (modoEdicao === 'nova-derivacao' && trechoAtivo) {
      await adicionarPonto(coordenadas);
    }
  };

  const iniciarNovoTrecho = () => {
    setModoEdicao('novo-trecho');
    setNomeNovoTrecho(`Trecho ${trechos.length + 1}`);
    setPontosMarcados([]);
    setSegmentos([]);
  };

  const iniciarNovaDerivacao = (trecho) => {
    setTrechoAtivo(trecho);
    setModoEdicao('nova-derivacao');
    setNomeNovaDerivacao(`Derivação ${(trecho.derivacoes?.length || 0) + 1}`);
    setPontosMarcados([]);
    setSegmentos([]);
  };

  // Função para calcular a distância entre dois pontos em metros (fórmula de Haversine)
  const calcularDistanciaEmMetros = (ponto1, ponto2) => {
    const R = 6371000; // Raio da Terra em metros
    const lat1 = ponto1[0] * Math.PI / 180;
    const lat2 = ponto2[0] * Math.PI / 180;
    const deltaLat = (ponto2[0] - ponto1[0]) * Math.PI / 180;
    const deltaLon = (ponto2[1] - ponto1[1]) * Math.PI / 180;

    const a = Math.sin(deltaLat/2) * Math.sin(deltaLat/2) +
              Math.cos(lat1) * Math.cos(lat2) *
              Math.sin(deltaLon/2) * Math.sin(deltaLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    const distancia = R * c;
    
    return distancia;
  };

  // Função para calcular a distância total de um trajeto em metros
  const calcularDistanciaTrajeto = (pontos) => {
    if (!pontos || pontos.length < 2) return 0;
    
    let distanciaTotal = 0;
    for (let i = 0; i < pontos.length - 1; i++) {
      distanciaTotal += calcularDistanciaEmMetros(pontos[i], pontos[i+1]);
    }
    
    return distanciaTotal;
  };

  // Função para obter todos os pontos da rota completa (combinando todos os segmentos)
  const obterRotaCompleta = () => {
    if (segmentos.length === 0) return pontosMarcados;
    
    let rotaCompleta = [];
    
    // Adicionar o primeiro ponto
    if (pontosMarcados.length > 0) {
      rotaCompleta.push(pontosMarcados[0]);
    }
    
    // Adicionar todos os segmentos (exceto o primeiro ponto de cada um para evitar duplicação)
    segmentos.forEach(segmento => {
      if (segmento.length > 1) {
        rotaCompleta = rotaCompleta.concat(segmento.slice(1));
      }
    });
    
    return rotaCompleta;
  };

  const confirmarTrecho = () => {
    if (!nomeNovoTrecho.trim() || pontosMarcados.length < 2) {
      alert('É necessário um nome e pelo menos 2 pontos para criar o trecho');
      return;
    }

    const rotaCompleta = obterRotaCompleta();
    const distanciaMetros = calcularDistanciaTrajeto(rotaCompleta);
    
    const novoTrecho = {
      id: Date.now(),
      nome: nomeNovoTrecho.trim(),
      tipo: 'principal',
      pontos: rotaCompleta,
      pontosOriginais: pontosMarcados, // Guardar pontos clicados pelo usuário
      distanciaMetros: distanciaMetros, // Distância em metros
      derivacoes: []
    };

    setTrechos(prev => [...prev, novoTrecho]);
    cancelarEdicao();
  };

  const confirmarDerivacao = () => {
    if (!nomeNovaDerivacao.trim() || pontosMarcados.length < 2) {
      alert('É necessário um nome e pelo menos 2 pontos para criar a derivação');
      return;
    }

    const rotaCompleta = obterRotaCompleta();
    const distanciaMetros = calcularDistanciaTrajeto(rotaCompleta);

    const novaDerivacao = {
      id: Date.now(),
      nome: nomeNovaDerivacao.trim(),
      pontos: rotaCompleta,
      pontosOriginais: pontosMarcados,
      distanciaMetros: distanciaMetros // Distância em metros
    };

    setTrechos(prev => prev.map(trecho => 
      trecho.id === trechoAtivo.id 
        ? { ...trecho, derivacoes: [...(trecho.derivacoes || []), novaDerivacao] }
        : trecho
    ));

    cancelarEdicao();
  };

  const cancelarEdicao = () => {
    setModoEdicao('visualizar');
    setTrechoAtivo(null);
    setPontosMarcados([]);
    setSegmentos([]);
    setNomeNovoTrecho('');
    setNomeNovaDerivacao('');
  };

  const excluirTrecho = (trechoId) => {
    if (window.confirm('Tem certeza que deseja excluir este trecho e todas suas derivações?')) {
      setTrechos(prev => prev.filter(t => t.id !== trechoId));
    }
  };

  const excluirDerivacao = (trechoId, derivacaoId) => {
    if (window.confirm('Tem certeza que deseja excluir esta derivação?')) {
      setTrechos(prev => prev.map(trecho => 
        trecho.id === trechoId 
          ? { ...trecho, derivacoes: trecho.derivacoes.filter(d => d.id !== derivacaoId) }
          : trecho
      ));
    }
  };

  const desfazerUltimoPonto = () => {
    if (pontosMarcados.length <= 1) {
      // Se só tem um ponto ou nenhum, limpar tudo
      setPontosMarcados([]);
      setSegmentos([]);
      return;
    }
    
    // Remover o último ponto
    setPontosMarcados(prev => prev.slice(0, -1));
    
    // Remover o último segmento
    setSegmentos(prev => prev.slice(0, -1));
  };

  const confirmarTrechos = () => {
    if (trechos.length === 0) {
      alert('É necessário adicionar pelo menos um trecho');
      return;
    }
    onTrechosAtualizados(trechos);
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ 
        maxWidth: '1200px', 
        width: '95%', 
        height: '90vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        <div className="modal-header">
          <h3 className="modal-title">{titulo}</h3>
          <button className="close-button" onClick={onFechar}>
            <X size={20} />
          </button>
        </div>

        <div style={{ 
          display: 'flex', 
          flex: 1,
          gap: '20px',
          overflow: 'hidden',
          minHeight: 0
        }}>
          {/* Painel lateral de controle */}
          <div style={{ width: '320px', backgroundColor: '#f8f9fa', borderRadius: '8px', padding: '15px', overflowY: 'auto' }}>
            
            {/* Instruções */}
            <div style={{ marginBottom: '20px', padding: '12px', backgroundColor: '#e3f2fd', borderRadius: '6px' }}>
              <h5 style={{ margin: '0 0 8px 0', color: '#1976d2' }}>
                <Navigation size={16} style={{ marginRight: '6px' }} />
                Roteamento Incremental
              </h5>
              <p style={{ margin: 0, fontSize: '12px', color: '#1565c0' }}>
                Clique nos pontos do mapa para criar o trajeto. O sistema calculará automaticamente a rota entre cada ponto, seguindo as ruas.
              </p>
            </div>

            {/* Controles principais */}
            <div style={{ marginBottom: '20px' }}>
              <h4 style={{ marginBottom: '10px' }}>Controles</h4>
              
              {modoEdicao === 'visualizar' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <button className="button" onClick={iniciarNovoTrecho}>
                    <Plus size={16} />
                    Novo Trecho
                  </button>
                </div>
              )}

              {modoEdicao === 'novo-trecho' && (
                <div>
                  <div className="form-group" style={{ marginBottom: '10px' }}>
                    <label>Nome do Trecho:</label>
                    <input
                      type="text"
                      value={nomeNovoTrecho}
                      onChange={(e) => setNomeNovoTrecho(e.target.value)}
                      placeholder="Ex: Rua Principal"
                    />
                  </div>
                  
                  <div style={{ marginBottom: '10px', padding: '8px', backgroundColor: '#fff3cd', borderRadius: '4px' }}>
                    <p style={{ margin: 0, fontSize: '12px', color: '#856404' }}>
                      <strong>Pontos marcados:</strong> {pontosMarcados.length}
                      {carregandoRota && <span> • Calculando rota...</span>}
                    </p>
                    <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#155724' }}>
                      <strong>Segmentos:</strong> {segmentos.length}
                    </p>
                    <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#155724' }}>
                      <strong>Instruções:</strong> Clique no mapa para adicionar pontos. O sistema calculará a rota entre eles.
                    </p>
                  </div>

                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button 
                      className="button secondary" 
                      onClick={desfazerUltimoPonto}
                      disabled={pontosMarcados.length === 0}
                      style={{ fontSize: '12px', padding: '6px 10px' }}
                    >
                      Desfazer
                    </button>
                    <button 
                      className="button" 
                      onClick={confirmarTrecho}
                      disabled={pontosMarcados.length < 2}
                      style={{ fontSize: '12px', padding: '6px 10px' }}
                    >
                      <Check size={12} />
                      Confirmar
                    </button>
                    <button 
                      className="button secondary" 
                      onClick={cancelarEdicao}
                      style={{ fontSize: '12px', padding: '6px 10px' }}
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              )}

              {modoEdicao === 'nova-derivacao' && (
                <div>
                  <div className="form-group" style={{ marginBottom: '10px' }}>
                    <label>Nome da Derivação:</label>
                    <input
                      type="text"
                      value={nomeNovaDerivacao}
                      onChange={(e) => setNomeNovaDerivacao(e.target.value)}
                      placeholder="Ex: Acesso à Escola"
                    />
                  </div>
                  
                  <div style={{ marginBottom: '10px', padding: '8px', backgroundColor: '#d4edda', borderRadius: '4px' }}>
                    <p style={{ margin: 0, fontSize: '12px', color: '#155724' }}>
                      <strong>Derivação de:</strong> {trechoAtivo?.nome}
                    </p>
                    <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#155724' }}>
                      <strong>Pontos marcados:</strong> {pontosMarcados.length}
                      {carregandoRota && <span> • Calculando rota...</span>}
                    </p>
                    <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#155724' }}>
                      <strong>Segmentos:</strong> {segmentos.length}
                    </p>
                  </div>

                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button 
                      className="button secondary" 
                      onClick={desfazerUltimoPonto}
                      disabled={pontosMarcados.length === 0}
                      style={{ fontSize: '12px', padding: '6px 10px' }}
                    >
                      Desfazer
                    </button>
                    <button 
                      className="button" 
                      onClick={confirmarDerivacao}
                      disabled={pontosMarcados.length < 2}
                      style={{ fontSize: '12px', padding: '6px 10px' }}
                    >
                      <Check size={12} />
                      Confirmar
                    </button>
                    <button 
                      className="button secondary" 
                      onClick={cancelarEdicao}
                      style={{ fontSize: '12px', padding: '6px 10px' }}
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Lista de trechos */}
            <div>
              <h4 style={{ marginBottom: '10px' }}>Trechos ({trechos.length})</h4>
              
              {trechos.length === 0 ? (
                <p style={{ fontSize: '14px', color: '#666', textAlign: 'center', padding: '20px' }}>
                  Nenhum trecho adicionado
                </p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {trechos.map(trecho => (
                    <div key={trecho.id} style={{
                      padding: '10px',
                      backgroundColor: 'white',
                      borderRadius: '6px',
                      border: '1px solid #dee2e6'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <strong style={{ fontSize: '14px' }}>{trecho.nome}</strong>
                        <button
                          className="button danger"
                          onClick={() => excluirTrecho(trecho.id)}
                          style={{ padding: '4px 8px', fontSize: '12px' }}
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                      
                      <p style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                        {trecho.pontos.length} pontos na rota
                        {trecho.distanciaMetros && (
                          <span> • <Ruler size={12} style={{ verticalAlign: 'middle' }} /> {(trecho.distanciaMetros / 1000).toFixed(2)} km</span>
                        )}
                      </p>

                      <button
                        className="button"
                        onClick={() => iniciarNovaDerivacao(trecho)}
                        style={{ padding: '4px 8px', fontSize: '12px', width: '100%' }}
                        disabled={modoEdicao !== 'visualizar'}
                      >
                        <Plus size={12} />
                        Adicionar Derivação
                      </button>

                      {/* Lista de derivações */}
                      {trecho.derivacoes && trecho.derivacoes.length > 0 && (
                        <div style={{ marginTop: '8px', paddingLeft: '10px' }}>
                          <p style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '5px' }}>
                            Derivações:
                          </p>
                          {trecho.derivacoes.map(derivacao => (
                            <div key={derivacao.id} style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '4px 8px',
                              backgroundColor: '#e9ecef',
                              borderRadius: '4px',
                              marginBottom: '4px'
                            }}>
                              <span style={{ fontSize: '12px' }}>
                                {derivacao.nome} 
                                {derivacao.distanciaMetros && (
                                  <span> • <Ruler size={10} style={{ verticalAlign: 'middle' }} /> {(derivacao.distanciaMetros / 1000).toFixed(2)} km</span>
                                )}
                              </span>
                              <button
                                className="button danger"
                                onClick={() => excluirDerivacao(trecho.id, derivacao.id)}
                                style={{ padding: '2px 6px', fontSize: '10px' }}
                              >
                                <Trash2 size={10} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Mapa */}
          <div style={{ flex: 1 }}>
            <MapContainer
              center={centroConceicaoCastelo}
              zoom={14}
              style={{ height: '100%', width: '100%', borderRadius: '8px' }}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              />
              
              <MapClickHandler 
                onMapClick={handleMapClick} 
                ativo={modoEdicao === 'novo-trecho' || modoEdicao === 'nova-derivacao'} 
              />
              
              {/* Renderizar trechos existentes */}
              {trechos.map(trecho => (
                <React.Fragment key={trecho.id}>
                  {/* Trecho principal */}
                  <Polyline
                    positions={trecho.pontos}
                    color="#007bff"
                    weight={4}
                    opacity={0.8}
                  />
                  
                  {/* Pontos originais marcados pelo usuário */}
                  {trecho.pontosOriginais && trecho.pontosOriginais.map((ponto, index) => (
                    <Marker key={`orig-${trecho.id}-${index}`} position={ponto}>
                      <Popup>
                        <div>
                          <strong>{trecho.nome}</strong><br/>
                          Ponto {index + 1} marcado pelo usuário
                        </div>
                      </Popup>
                    </Marker>
                  ))}
                  
                  {/* Derivações */}
                  {trecho.derivacoes && trecho.derivacoes.map(derivacao => (
                    <React.Fragment key={derivacao.id}>
                      <Polyline
                        positions={derivacao.pontos}
                        color="#28a745"
                        weight={3}
                        opacity={0.7}
                        dashArray="5, 5"
                      />
                      
                      {/* Pontos originais da derivação */}
                      {derivacao.pontosOriginais && derivacao.pontosOriginais.map((ponto, index) => (
                        <Marker key={`deriv-${derivacao.id}-${index}`} position={ponto}>
                          <Popup>
                            <div>
                              <strong>{derivacao.nome}</strong><br/>
                              Ponto {index + 1} marcado pelo usuário
                            </div>
                          </Popup>
                        </Marker>
                      ))}
                    </React.Fragment>
                  ))}
                </React.Fragment>
              ))}
              
              {/* Pontos temporários sendo marcados */}
              {pontosMarcados.map((ponto, index) => (
                <Marker key={`temp-${index}`} position={ponto}>
                  <Popup>
                    <div>
                      Ponto {index + 1}
                      {index === 0 && <span><br/>Início</span>}
                      {index === pontosMarcados.length - 1 && index > 0 && <span><br/>Último ponto</span>}
                    </div>
                  </Popup>
                </Marker>
              ))}
              
              {/* Segmentos de rota calculados */}
              {segmentos.map((segmento, index) => (
                <Polyline
                  key={`segmento-${index}`}
                  positions={segmento}
                  color={modoEdicao === 'novo-trecho' ? '#007bff' : '#28a745'}
                  weight={modoEdicao === 'novo-trecho' ? 4 : 3}
                  opacity={0.6}
                  dashArray={modoEdicao === 'nova-derivacao' ? '5, 5' : null}
                />
              ))}
            </MapContainer>
          </div>
        </div>

        {/* Botões de ação */}
        <div style={{ 
          display: 'flex', 
          gap: '10px', 
          justifyContent: 'flex-end', 
          padding: '15px 20px',
          borderTop: '1px solid #dee2e6',
          flexShrink: 0
        }}>
          <button className="button secondary" onClick={onFechar}>
            Cancelar
          </button>
          <button 
            className="button" 
            onClick={confirmarTrechos}
            disabled={trechos.length === 0}
          >
            <Save size={16} />
            Salvar Trechos ({trechos.length})
          </button>
        </div>
      </div>
    </div>
  );
};

export default DesenharTrajeto;